import { useState } from 'react';

import { useLanguage } from '../contexts/LanguageContext';
import { Check, Zap } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';

const content = {
  ar: {
    badge: 'الأسعار',
    h1: 'أسعار واضحة وشفافة',
    sub: 'ابدأ مجاناً. توسّع مع نموّك.',
    toggle: { monthly: 'شهري', annual: 'سنوي', save: 'وفّر ٢٠٪' },
    plans: [
      {
        name: 'المبتدئ',
        price: 'مجاناً',
        period: '',
        desc: 'للأفراد المستكشفين لكويدوري',
        features: ['وصول لـ QuidPulse (محدود)', '٥٠ استعلام ذكاء اصطناعي / شهر', '١٠ منشورات / شهر', 'محتوى عربي + إنجليزي', 'قناة تيليغرام (١)', 'دعم المجتمع'],
        cta: 'ابدأ الآن',
        popular: false,
      },
      {
        name: 'المحترف',
        price: '$49',
        period: '/ شهر',
        desc: 'للمنشئين والأعمال الجادة',
        features: ['QuidPulse + Quidanalyzer كاملاً', 'استعلامات ذكاء اصطناعي غير محدودة', 'نشر غير محدود', 'Post Publisher + Video Publisher', 'تيليغرام + واتساب (٣ قنوات)', 'دعم ذو أولوية', 'لوحة تحليلات', 'تدريب ذكاء اصطناعي مخصص'],
        cta: 'ابدأ التجربة المجانية',
        popular: true,
      },
      {
        name: 'المؤسسي',
        price: 'مخصص',
        period: '',
        desc: 'للمؤسسات ذات الاحتياجات المتقدمة',
        features: ['وصول كامل للمنصة', 'خيارات العلامة التجارية الخاصة', 'تطوير وكلاء مخصصة', 'جميع تكاملات القنوات', 'بنية تحتية مخصصة', 'دعم مدعوم بـ SLA', 'تكاملات مخصصة', 'إعداد وتدريب'],
        cta: 'تواصل مع المبيعات',
        popular: false,
      },
    ],
    faq: {
      h: 'أسئلة شائعة',
      items: [
        { q: 'هل هناك تجربة مجانية؟', a: 'نعم، الخطة المبتدئة مجانية تماماً مع إمكانيات محدودة. يمكنك البدء فوراً دون بطاقة ائتمان.' },
        { q: 'هل يمكنني تغيير خطتي لاحقاً؟', a: 'نعم، يمكنك الترقية أو التخفيض في أي وقت. تنعكس التغييرات على الفور في حسابك.' },
        { q: 'ما القنوات المدعومة؟', a: 'حالياً: تيليغرام وواتساب. المزيد من القنوات قادم قريباً.' },
        { q: 'كيف يعمل تدريب الذكاء الاصطناعي المخصص؟', a: 'في خطة المحترف والمؤسسي، يمكننا تدريب نماذج مخصصة على بيانات مجالك الخاص.' },
      ],
    },
  },
  en: {
    badge: 'Pricing',
    h1: 'Simple, Transparent Pricing',
    sub: 'Start free. Scale as you grow.',
    toggle: { monthly: 'Monthly', annual: 'Annual', save: 'Save 20%' },
    plans: [
      {
        name: 'Starter',
        price: 'Free',
        period: '',
        desc: 'For individuals exploring Quidory',
        features: ['QuidPulse access (limited)', '50 AI queries / month', '10 published posts / month', 'Arabic + English content', 'Telegram channel (1)', 'Community support'],
        cta: 'Get Started',
        popular: false,
      },
      {
        name: 'Professional',
        price: '$49',
        period: '/ month',
        desc: 'For serious creators and businesses',
        features: ['Full QuidPulse + Quidanalyzer', 'Unlimited AI queries', 'Unlimited publishing', 'Post + Video Publisher', 'Telegram + WhatsApp (3 channels)', 'Priority support', 'Analytics dashboard', 'Custom AI training'],
        cta: 'Start Free Trial',
        popular: true,
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        desc: 'For organizations with advanced needs',
        features: ['Full platform access', 'White-label options', 'Custom AI agent development', 'All channel integrations', 'Dedicated infrastructure', 'SLA-backed support', 'Custom integrations', 'Onboarding & training'],
        cta: 'Contact Sales',
        popular: false,
      },
    ],
    faq: {
      h: 'Frequently Asked Questions',
      items: [
        { q: 'Is there a free trial?', a: 'Yes, the Starter plan is completely free with limited capabilities. You can start immediately without a credit card.' },
        { q: 'Can I change my plan later?', a: 'Yes, you can upgrade or downgrade at any time. Changes are reflected immediately in your account.' },
        { q: 'Which channels are supported?', a: 'Currently: Telegram and WhatsApp. More channels are coming soon.' },
        { q: 'How does custom AI training work?', a: 'In the Professional and Enterprise plans, we can train custom models on your domain-specific data.' },
      ],
    },
  },
};

const PricingPage = () => {
  const { language } = useLanguage();
  const c = content[language];
  const [faqOpen, setFaqOpen] = useState(null);

  return (
    <div className="pt-16">
      <SEO
        titleEn="Pricing — Start Free, Scale as You Grow | Quidory"
        titleAr="الأسعار — ابدأ مجاناً، توسّع مع نموّك | كويدوري"
        descEn="Simple, transparent pricing for Quidory's AI platform. Free Starter, Professional at $49/month, and Enterprise custom plans."
        descAr="أسعار واضحة وشفافة لمنصة كويدوري. خطة مجانية، محترفة بـ $٤٩ شهرياً، وخطط مؤسسية مخصصة."
        path="/pricing"
      />
      {/* Hero */}
      <section className="py-24 lg:py-28 bg-background text-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-4 block">{c.badge}</span>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-foreground mb-4">{c.h1}</h1>
        <p className="text-lg text-muted-foreground">{c.sub}</p>
      </section>

      {/* Plans */}
      <section className="pb-24 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {c.plans.map((plan, i) => (
              <div
                key={i}
                data-testid={`pricing-plan-${i}`}
                className={`relative p-8 rounded-2xl border transition-all duration-300 flex flex-col ${
                  plan.popular
                    ? 'border-gold/40 bg-card shadow-lg shadow-gold/5 scale-[1.02]'
                    : 'border-border bg-card hover:border-border/80'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gold text-background shadow-lg">
                      <Zap className="h-3 w-3" />
                      {language === 'ar' ? 'الأكثر شعبية' : 'Most Popular'}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{plan.desc}</p>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-light tracking-tight ${plan.popular ? 'text-gold' : 'text-foreground'}`}>
                      {plan.price}
                    </span>
                    {plan.period && <span className="text-muted-foreground text-sm">{plan.period}</span>}
                  </div>
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((feat, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${plan.popular ? 'text-gold' : 'text-muted-foreground'}`} />
                      <span className="text-sm text-muted-foreground">{feat}</span>
                    </li>
                  ))}
                </ul>

                <LocaleLink
                  to={i === 2 ? '/contact' : '/signup'}
                  data-testid={`plan-cta-${i}`}
                  className={`block text-center py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    plan.popular
                      ? 'bg-gold text-background hover:bg-gold/90'
                      : 'border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {plan.cta}
                </LocaleLink>
              </div>
            ))}
          </div>

          {/* Quidanalyzer cross-link — minimal, single-line discoverability */}
          <div className="mt-10 text-center">
            <LocaleLink
              to="/quidanalyzer"
              data-testid="pricing-qa-link"
              className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 underline-offset-4 hover:underline transition-colors"
            >
              {language === 'ar'
                ? '← اطّلع على باقات Quidanalyzer للأفراد ($15/شهر)'
                : 'See Quidanalyzer plans for individuals ($15/mo) →'}
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card border-t border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-light text-foreground mb-8 text-center">{c.faq.h}</h2>
          <div className="space-y-3">
            {c.faq.items.map((item, i) => (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className="border border-border rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-start text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  {item.q}
                  <span className={`text-muted-foreground transition-transform ${faqOpen === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {faqOpen === i && (
                  <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
